import React, { useState, useEffect } from 'react';
import { postApi, getApi } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Withdraw = () => {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [walletType, setWalletType] = useState('main'); 
  
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [stats, setStats] = useState({ available: 0, withdrawn: 0 });
  const [userData, setUserData] = useState(null);

  const FEE = 1.00;
  const MIN_WITHDRAW = 10;
  const netAmount = amount && parseFloat(amount) > 0 ? (parseFloat(amount) - FEE).toFixed(2) : '0.00';

  const fetchHistory = async () => {
    try {
      const formData = new FormData();
      const response = await postApi('withdraw/list', formData);
      if (response?.data?.status) {
        const payload = response.data.data;
        setHistory(payload?.data || []);
        setStats({
          available: payload?.trade_available || 0,
          withdrawn: payload?.withdrawal_amount || 0,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await getApi('profile');
      if (response.data?.status === true || response.status === true) {
        const user = response.data?.USER || response.USER;
        setUserData(user);
        if (user?.wallet_address) {
          setAddress(user.wallet_address);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchProfile();
  }, []);

  const isValidAddress = /^0x[a-fA-F0-9]{40}$/.test(address);
  const isFormValid = amount >= MIN_WITHDRAW && isValidAddress && password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) < MIN_WITHDRAW) {
      toast.error(`Minimum withdrawal is $${MIN_WITHDRAW}`);
      return;
    }
    if (!address) {
      toast.error('Please enter your wallet address');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('to_address', address);
      formData.append('password', password);
      formData.append('walletType', walletType);

      const response = await postApi('withdraw/start', formData);
      if (response?.data?.status) {
        toast.success(response.data.message || 'Withdraw request submitted successfully');
        setAmount('');
        setPassword('');
        setShowModal(false);
        fetchHistory(); // refresh the list
      } else {
        toast.error(response?.data?.message || 'Failed to submit withdrawal');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'An error occurred while submitting withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingWithdrawal = history.filter(h => h.status === 'pending').reduce((acc, h) => acc + parseFloat(h.withdraw_amount || 0), 0);

  return (
    <div className="page-wrapper">
      <ToastContainer position="top-right" autoClose={4000} theme="dark" />
      {/* Header */}
      <div className="page-header mb-4">
        <div className="section-label">Transfer</div>
        <h2 className="page-title">Withdraw</h2>
        <p className="page-subtitle">Transfer your earnings to your wallet</p>
      </div>

      {/* Quick Stats */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Available Balance', value: `$${stats.available.toFixed(2)}`, icon: 'bi-wallet2', color: 'green' },
          { label: 'Pending Withdrawal', value: `$${pendingWithdrawal.toFixed(2)}`, icon: 'bi-clock-history', color: 'yellow' },
          { label: 'Total Withdrawn', value: `$${stats.withdrawn.toFixed(2)}`, icon: 'bi-box-arrow-up-right', color: 'blue' },
        ].map((card, i) => (
          <div key={i} className="col-md-4">
            <div className={`metric-card ${card.color}`}>
              <div className={`metric-icon ${card.color}`}><i className={`bi ${card.icon}`}></i></div>
              <div>
                <div className="metric-label">{card.label}</div>
                <div className="metric-value">{card.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        {/* History */}
        <div className="col-12">
          <div className="p-card">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p className="section-label mb-0">Transactions</p>
                <h5 style={{ margin: 0, fontWeight: 700 }}>Withdrawal History</h5>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: 20, color: 'var(--text-secondary)' }}>
                  {history.length} records
                </span>
                <button className="btn btn-gradient" style={{ padding: '0.4rem 1rem', borderRadius: 8 }} onClick={() => setShowModal(true)}>
                  <i className="bi bi-plus-circle me-2"></i> New Withdraw
                </button>
              </div>
            </div>

            <div className="p-table-wrap">
              {loadingHistory ? (
                <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
              ) : history.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><i className="bi bi-box-arrow-up-right"></i></div>
                  <h6>No Withdrawals Yet</h6>
                  <p>Your withdrawal history will appear here</p>
                </div>
              ) : (
                <table className="p-table">
                  <thead>
                    <tr><th>ID</th><th>Amount</th><th>Address</th><th>Fee</th><th>Date</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {history.map(item => (
                      <tr key={item.id}>
                        <td className="mono dim">#{item.id}</td>
                        <td style={{ fontWeight: 700, color: 'var(--page-accent)' }}>${item.withdraw_amount}</td>
                        <td><span className="u-code" style={{ fontSize: '0.75rem' }} title={item.to_address}>
                          {item.to_address ? `${item.to_address.slice(0, 6)}...${item.to_address.slice(-4)}` : '—'}
                        </span></td>
                        <td className="dim">${item.fees_deduction}</td>
                        <td className="dim" style={{ fontSize: '0.8rem' }}>{item.date_time?.split(' ')[0]}</td>
                        <td><span className={`s-pill ${item.status === 'success' ? 'success' : item.status === 'pending' ? 'pending' : 'review'}`}>{item.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Info Card */}
          <div className="p-card mt-4" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.05), rgba(0,255,149,0.03))', borderColor: 'rgba(0,212,255,0.2)' }}>
            <p className="section-label">Withdrawal Policy</p>
            <div className="row g-3">
              {[
                { icon: 'bi-clock', title: 'Processing Time', desc: '1–24 hours for approval' },
                { icon: 'bi-shield', title: 'Security', desc: 'Manual review for safety' },
                { icon: 'bi-calendar', title: 'Schedule', desc: 'Anytime, 24/7' },
                { icon: 'bi-cash-coin', title: 'Min Withdrawal', desc: `$${MIN_WITHDRAW} USDT` },
              ].map((info, i) => (
                <div key={i} className="col-6">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className={`bi ${info.icon}`} style={{ fontSize: '1.1rem', color: 'var(--page-blue)', flexShrink: 0 }}></i>
                    <div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white' }}>{info.title}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{info.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', zIndex: 1040, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="p-card" style={{ width: '100%', maxWidth: 500, zIndex: 1050, position: 'relative', overflowY: 'auto', maxHeight: '90vh' }}>
            <button 
              onClick={() => setShowModal(false)}
              style={{ position: 'absolute', top: 15, right: 15, background: 'transparent', border: 'none', color: 'white', fontSize: '1.2rem' }}>
              <i className="bi bi-x-lg"></i>
            </button>
            <h5 style={{ fontWeight: 700, marginBottom: '1.5rem' }}>Withdraw Funds</h5>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="p-label">Wallet Type</label>
                <div className="address-box">
                  <i className="bi bi-box" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <select
                    value={walletType}
                    onChange={e => setWalletType(e.target.value)}
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.85rem' }}
                  >
                    <option value="main" style={{ color: 'black' }}>Main Wallet</option>
                    <option value="level" style={{ color: 'black' }}>Level Wallet</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="p-label">Wallet Address (USDT BEP-20)</label>
                <div className="address-box">
                  <i className="bi bi-wallet2" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <input
                    type="text"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Enter your BEP-20 address..."
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.85rem', fontFamily: 'JetBrains Mono, monospace' }}
                  />
                  {address && (isValidAddress ? <i className="bi bi-check-circle text-success ms-2"></i> : <i className="bi bi-x-circle text-danger ms-2"></i>)}
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <label className="p-label mb-0">Amount (USDT)</label>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Min: ${MIN_WITHDRAW}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', padding: '0 1rem' }}>
                  <span style={{ color: 'var(--page-accent)', fontWeight: 800, fontSize: '1.1rem', marginRight: 8 }}>$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="0.00"
                    style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.3rem', padding: '0.9rem 0', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, flex: 1, outline: 'none' }}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="p-label">Account Password</label>
                <div className="address-box">
                  <i className="bi bi-lock" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password..."
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* Fee Breakdown */}
              <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 14, padding: '1rem 1.2rem', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                {[
                  { label: 'Withdrawal Amount', value: `$${amount || '0.00'}` },
                  { label: 'Network Fee', value: `-$${FEE}`, color: 'var(--page-danger)' },
                  { label: 'You Receive', value: `$${netAmount}`, color: 'var(--page-accent)', bold: true },
                ].map((row, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{row.label}</span>
                    <span className="i-value" style={{ color: row.color || 'white', fontFamily: 'JetBrains Mono, monospace', fontWeight: row.bold ? 800 : 600 }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              <button type="submit" className="btn btn-gradient w-100" disabled={submitting || !isFormValid} style={{ borderRadius: 14, padding: '0.9rem', fontWeight: 700, fontSize: '1rem' }}>
                {submitting
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                  : <><i className="bi bi-arrow-up-circle me-2"></i>Submit Withdrawal</>
                }
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdraw;